import { UpOutlined } from '@ant-design/icons';
import { Collapse, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React from 'react';
import { statusApi } from '../../../api';
import constants from '../../../constants';
import { ScrollType } from '../../../Helper';
import { PerformanceData, WidgetInfo } from '../../../model/StatusModel';
import './performanceStatus.scss';

const { Panel } = Collapse;

// Internal state for the component
interface State {
	isLoading: boolean;
	data: PerformanceData;
	isExpanded: boolean;
}

// Normal properties for the component
interface OwnProps {
	widgetInfo: WidgetInfo;
}

// combine them together
type Props = OwnProps;

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 379, y: 312 };
const columns: ColumnsType<any> = [
	{
		title: _('Type'),
		dataIndex: 'Type',
		align: 'left',
	},
	{
		title: _('Description'),
		dataIndex: 'Description',
		align: 'left',
	},
];

class PerformanceStatus extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);

		this.state = {
			isLoading: true,
			data: null,
			isExpanded: true,
		};

		//We use this flag to avoid multiple api calls at the same time
		window.sessionStorage.setItem(constants.sessionStorage.statusDashboard.RETRIEVE_PERFORMANCE_STATUS_FLAG, '0');
	}

	componentDidMount() {
		this.fetchData();
	}

	componentWillUnmount() {
		this.clearFetchTimeout();
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	clearFetchTimeout() {
		if (this.autoRefreshPerformanceLoop) {
			clearTimeout(this.autoRefreshPerformanceLoop);
		}
	}

	private autoRefreshPerformanceLoop: null | ReturnType<typeof setTimeout> = null;

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		const { isExpanded } = this.state;

		if (nextState.isExpanded) {
			this.clearFetchTimeout();

			if (!isExpanded) {
				this.fetchData();
			} else {
				const { widgetInfo } = this.props;

				if (widgetInfo.autoRefresh) {
					this.autoRefreshPerformanceLoop = setTimeout(() => {
						this.fetchData();
					}, widgetInfo.autoRefreshTimer * 1000);
				}
			}
		}

		return JSON.stringify(this.state.data) !== JSON.stringify(nextState.data);
	}

	fetchData() {
		const sessionStorageKey = constants.sessionStorage.statusDashboard.RETRIEVE_PERFORMANCE_STATUS_FLAG;
		if (window.sessionStorage.getItem(sessionStorageKey) === '0') {
			window.sessionStorage.setItem(sessionStorageKey, '1');

			statusApi
				.retrievePerformanceStatus()
				.then(response => {
					window.sessionStorage.setItem(sessionStorageKey, '0');

					this.setState({
						isLoading: false,
						data: response,
					});
				})
				.catch(e => console.log(e));
		}
	}

	handleOnChange(isExpanded) {
		if (isExpanded) {
			this.setState({
				isLoading: true,
				isExpanded: true,
			});
		} else {
			this.setState({
				isExpanded: false,
			});
		}
	}

	render() {
		const { isLoading, data } = this.state;

		let content;
		let tableData = [];
		if (!isLoading) {
			let lastBackUp = [];
			let cpuDrives = [];
			let dbDrives = [];
			let services = [];
			let globalIndex = 0;

			if (data && data.DrivesSizeApp.length) {
				cpuDrives = data.DrivesSizeApp.map(drive => {
					globalIndex++;
					return {
						key: `VelocityApplicationServer-DriveSize-${globalIndex}`,
						Type: drive.Item1 ? _('HowMuchFreeSpaceIsOnTheInstallationDrive') : _('HowMuchFreeSpace'),
						Description: drive.Item2,
					};
				});
				dbDrives = data.DrivesSizeDb.map(drive => {
					globalIndex++;
					return {
						key: `VelocitySQLDBServer-DriveSize-${globalIndex}`,
						Type: drive.Item1 ? _('HowMuchFreeSpaceIsOnTheInstallationDrive') : _('HowMuchFreeSpace'),
						Description: drive.Item2,
					};
				});
				services = data.VelocityServices.map(service => {
					globalIndex += 4;
					return [
						{ key: `Services-${globalIndex - 3}`, Type: service.Name, Description: '' },
						{ key: `Services-CpuUtilization-${globalIndex - 2}`, Type: _('CpuUtilization'), Description: service.CpUsage },
						{ key: `Services-MemoryUsage-${globalIndex - 1}`, Type: _('MemoryUsage'), Description: service.MemoryUsage },
						{ key: `Services-Status-${globalIndex}`, Type: _('Status'), Description: service.Status },
					];
				});

				globalIndex += 2;
				cpuDrives.unshift(
					{ key: `VelocityApplicationServer-${globalIndex - 1}`, Type: _('VelocityApplicationServer'), Description: '' },
					{ key: `VelocityApplicationServer-CpuUtilization-${globalIndex}`, Type: _('CpuUtilization'), Description: data.CpUsage }
				);

				if (data.HasBackUp) {
					globalIndex++;
					lastBackUp.push({
						key: `VelocitySQLDBServer-LastBackupDate-${globalIndex}`,
						Type: _('LastBackupDate'),
						Description: FormatDate(data.LastBackupDate, true, null, true, false),
					});
				}

				globalIndex += 4;
				dbDrives.unshift(
					{
						key: `VelocitySQLDBServer-${globalIndex}`,
						Type: _('VelocitySQLDBServer'),
						Description: '',
					},
					{
						key: `VelocitySQLDBServer-CpuUtilization-${globalIndex - 1}`,
						Type: _('CpuUtilization'),
						Description: data.DbCpUsage,
					},
					{
						key: `VelocitySQLDBServer-DbSize-${globalIndex - 2}`,
						Type: _('DbSize'),
						Description: data.DbSize,
					},
					{
						key: `VelocitySQLDBServer-LogSize-${globalIndex - 3}`,
						Type: _('LogSize'),
						Description: data.LogSize,
					},
					...lastBackUp
				);

				let servicesRows = services.flat();
				tableData = [...cpuDrives, ...dbDrives, ...servicesRows];
			}

			content = (
				<Table
					id='PerformanceStatusTable'
					columns={columns}
					dataSource={tableData}
					rowClassName={record => (record.Description === '' ? 'status-performance-title-row' : '')}
					scroll={scroll}
					pagination={false}
					size='small'
					className={'status-widget-table status-widget-performance-status'}
				/>
			);
		}

		return (
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className='spin-container'>
				<div className='performance-status-container'>
					<Collapse
						bordered={true}
						defaultActiveKey={'1'}
						expandIconPosition={'right'}
						expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 180 : 0} />}
						onChange={(keys: string[]) => {
							this.handleOnChange(keys.length === 1);
						}}
						className={'status-widget-collapse'}>
						<Panel id='statusDashboardPerformanceStatusWidget' header={_('PerformanceStatus')} key='1' className='status-widget'>
							{content}
						</Panel>
					</Collapse>
				</div>
			</Spin>
		);
	}
}

export { PerformanceStatus };
